import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
// add-edit-journal-entry.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'; // Import FormBuilder, etc.
import { ActivatedRoute } from '@angular/router'; // For route parameters
import { Location, CommonModule } from '@angular/common';
import { JournalEntriesService } from "../journal-entries.service";
import { JournalEntry } from "../models/journal-entry-header.model";
import { JournalItem } from "../models/journal-entry-detail.model";
import { MessageService } from "primeng/api";
import { ChartOfAccounts } from "../../../chart-of-accounts/chart-of-accounts.model";
import { ChartOfAccountsService } from "../../../chart-of-accounts/chart-of-accounts.service";
import { SessionStorageService } from "../../../services/session-storage.service";
import { AutoCompleteModule, AutoCompleteSelectEvent } from "primeng/autocomplete";
import { Journal } from "../models/journal.model";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { debounceTime, distinctUntilChanged, finalize, Subscription } from "rxjs"; // For navigating back
import { DimensionTypeService } from "../dimension-type.service";
import { DimensionType } from "../models/dimension-type.model";
import { DimensionValue } from "../models/dimension-value.model";
import { JournalEntryLineDimension } from "../models/journal-entry-detail-dimension.model";
import { VehicleService } from "../../../fleet/vehicle/vehicle.service";
import { ContractsService } from "../../../contracts/contracts.service";
import { CostCenterService } from "../../cost-center/cost-center.service";
import { PartyService } from "../../../party/party.service";
import { Party } from "../../../party/party.model";
import { PartyType } from 'src/app/party/party-type.enum';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';

interface partner {
    partnerId: number;
    partnerName: string;
}

@Component({
    selector: 'app-add-edit-journal-entry',
    templateUrl: './add-edit-journal-entry.component.html',
    styleUrls: ['./add-edit-journal-entry.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        AutoCompleteModule,
        ButtonModule,
        TooltipModule,
        InputNumberModule,
        ToastModule,
        ProgressBarModule,
        DividerModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageMetricStripComponent
    ],
    providers: [
        JournalEntriesService,
        MessageService,
        ChartOfAccountsService,
        SessionStorageService,
        DimensionTypeService,
        VehicleService,
        ContractsService,
        CostCenterService,
        PartyService
    ]
})
export class AddEditJournalEntryComponent implements OnInit, OnDestroy {

    companyId: number;
    journalEntryForm!: FormGroup;
    journalEntry: JournalEntry = new JournalEntry();
    editMode: boolean = false;
    journalEntryId?: number;

    partyList: Party[] = [];

    journalList: Journal[] = [];
    chartOfAccounts: ChartOfAccounts[] = []; // Array to store chart of accounts
    filteredChartOfAccounts: ChartOfAccounts[] = []; // For autocomplete filtering

    isLoading = false;
    isSaving = false;

    // Properties for totals
    totalDebit: number = 0;
    totalCredit: number = 0;
    private journalItemsSub: Subscription;

    // --- NEW: Properties for Dimensions ---
    dimensionTypes: DimensionType[] = [];
    // Renamed for clarity: This caches the OPTIONS for the dropdowns, keyed by dimensionTypeId.
    dimensionOptionsCache: Map<number, DimensionValue[]> = new Map();

    canSave: boolean = false;

    constructor(private fb: FormBuilder, private route: ActivatedRoute,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService, private vehicleService: VehicleService,
        private contractService: ContractsService, private costCenterService: CostCenterService,
        private location: Location, private chartOfAccountsService: ChartOfAccountsService, private partyService: PartyService,
        private dimensionTypeService: DimensionTypeService,
        private journalEntriesService: JournalEntriesService,
        private permissionService: PermissionService) {
        this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
    } // Inject FormBuilder

    ngOnInit(): void {
        this.journalEntryForm = this.fb.group({
            entryId: [''],
            entryNumber: [''],
            reference: ['', Validators.required],
            journal: ['', Validators.required],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            entryDate: [new Date(), Validators.required],  // Initialize with current date
            journalItems: this.fb.array([this.createJournalItemsFormGroup()])  // Start with one detail row
        });

        this.route.params.subscribe(params => {
            this.journalEntryId = +params['id'];  // + converts to number
            if (this.journalEntryId) {
                this.editMode = true;
                this.canSave = this.permissionService.hasPermission(app_permissions.accounts.journal.edit);
                this.loadJournalEntry(this.journalEntryId);
            } else {
                this.canSave = this.permissionService.hasPermission(app_permissions.accounts.journal.create);
            }

            if (!this.canSave) {
                this.journalEntryForm.disable({ emitEvent: false });
            }
        });

        this.chartOfAccountsService.getAllChartOfAccountsByCompany(this.companyId, false).subscribe(accounts => {
            this.chartOfAccounts = accounts;
        });

        this.journalEntriesService.getAllJournalsByCompany(this.companyId).subscribe(journals => {
            this.journalList = journals;
        });

        this.partyService.getParties(this.companyId, [], "", 0, 1000).subscribe(parties => {
            this.partyList = parties.content;
        });

        this.dimensionTypeService.getDimensionTypesByCompany(this.companyId).subscribe(types => {
            this.dimensionTypes = types;
        });

        this.subscribeToJournalItemsChanges();
    }

    ngOnDestroy(): void {
        if (this.journalItemsSub) {
            this.journalItemsSub.unsubscribe();
        }
    }

    createJournalItemsFormGroup(journalItem?: JournalItem): FormGroup {
        if (journalItem)
            return this.fb.group({
                itemId: [journalItem?.itemId ?? 0],
                accountCode: [(journalItem?.account?.accountCode ?? '') + ' ' + (journalItem?.account?.accountName ?? ''), Validators.required],
                party: [journalItem?.party ?? null], // partnerId from backend maps to partyId
                logisticsProjectId: [journalItem?.logisticsProjectId ?? this.journalEntryForm?.get('logisticsProjectId')?.value ?? null],
                debitAmount: [journalItem?.debit ?? 0, Validators.min(0)],  // Initialize debit/credit to 0
                creditAmount: [journalItem?.credit ?? 0, Validators.min(0)],
                dimensions: this.fb.array(journalItem?.dimensions ? journalItem.dimensions.map(dim => this.createDimensionFormGroup(dim)) : [])
            });
        else {
            return this.fb.group({
                itemId: [],
                accountCode: [null, Validators.required],
                party: [null],
                logisticsProjectId: [this.journalEntryForm?.get('logisticsProjectId')?.value ?? null],
                debitAmount: [null, Validators.min(0)],  // Initialize debit/credit to 0
                creditAmount: [null, Validators.min(0)],
                dimensions: this.fb.array([]) // Start with empty dimensions
            });
        }
    }

    get getJournalEntryDetailsControls() {  // Getter for details controls
        const detailsArray = this.journalEntryForm.get('journalItems') as FormArray;
        return detailsArray ? detailsArray.controls : [];
    }

    // --- NEW: Methods for managing Dimensions ---

    createDimensionFormGroup(dimension?: JournalEntryLineDimension): FormGroup {
        return this.fb.group({
            journalEntryLineDimensionId: [dimension?.journalEntryLineDimensionId],
            // FIX: Correctly access the nested property when loading existing data
            dimensionTypeId: [dimension?.dimensionType?.dimensionTypeId, Validators.required],
            dimensionValue: [{
                id: dimension?.dimensionValueId,
                name: dimension?.dimensionValueName
            }, Validators.required]
        });
    }

    getDimensionsControls(itemIndex: number): any[] {
        const dimensionsArray = (this.journalEntryForm.get('journalItems') as FormArray)
            .at(itemIndex).get('dimensions') as FormArray;
        return dimensionsArray.controls;
    }

    addDimension(itemIndex: number): void {
        const dimensionsArray = (this.journalEntryForm.get('journalItems') as FormArray)
            .at(itemIndex).get('dimensions') as FormArray;
        dimensionsArray.push(this.createDimensionFormGroup());
    }

    removeDimension(itemIndex: number, dimensionIndex: number): void {
        const dimensionsArray = (this.journalEntryForm.get('journalItems') as FormArray)
            .at(itemIndex).get('dimensions') as FormArray;
        dimensionsArray.removeAt(dimensionIndex);
    }

    addJournalEntryDetail(journalEntryDetail?: JournalItem) {
        (this.journalEntryForm.get('journalItems') as FormArray).push(this.createJournalItemsFormGroup(journalEntryDetail));
    }

    loadJournalEntry(id: number) {
        this.isLoading = true;
        this.journalEntriesService.getJournalEntryById(id).subscribe(entry => {
            this.journalEntry = entry; // Assign the retrieved entry

            // Create a version of the entry data without the items array for patching the main form.
            const { items, ...entryData } = entry;
            const patchData = { ...entryData, entryDate: entry.entryDate ? new Date(entry.entryDate) : null };
            this.journalEntryForm.patchValue(patchData);

            // Create a new FormArray with all the items from the loaded entry and set it on the form.
            const itemsFormArray = this.fb.array(entry.items.map(item => this.createJournalItemsFormGroup(item)));
            this.journalEntryForm.setControl('journalItems', itemsFormArray);
            this.subscribeToJournalItemsChanges();
            this.calculateTotals();

            this.isLoading = false;
        });
    }

    filterChartOfAccounts(event: any) {
        let filtered: any[] = [];
        let query = event.query;
        for (let i = 0; i < this.chartOfAccounts.length; i++) {
            let account = this.chartOfAccounts[i];
            if (account.accountName.toLowerCase().indexOf(query.toLowerCase()) == 0
                || account.accountCode.toLowerCase().indexOf(query.toLowerCase()) == 0) {
                filtered.push(account);
            }
        }
        this.filteredChartOfAccounts = filtered;
    }

    saveJournalEntry() {
        if (this.isSaving || this.isLoading) return;
        if (!this.canSave) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to save this journal entry.' });
            return;
        }
        this.journalEntryForm.markAllAsTouched(); // Mark all fields as touched to show validation errors
        if (this.journalEntryForm.invalid) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields.' });
            return;
        }
        if (this.isTotalMismatch()) {
            this.messageService.add({ severity: 'error', summary: 'Balance Error', detail: 'Total debit and credit amounts must be equal and not zero.' });
            return;
        }

        const journalEntryData = this.journalEntryForm.value;

        //this.journalEntry = journalEntryData;
        if (this.editMode)
            this.journalEntry.entryId = this.journalEntryId; // Let backend handle ID for new entries
        this.journalEntry.entryNumber = this.journalEntryForm.value.entryNumber || undefined; // Send if available, or let backend generate
        this.journalEntry.reference = this.journalEntryForm.value.reference;
        // Assuming formValues.journal is the Journal object selected from dropdown
        this.journalEntry.journal = this.journalEntryForm.value.journal;
        this.journalEntry.logisticsProjectId = this.journalEntryForm.value.logisticsProjectId || undefined;
        this.journalEntry.entryDate = this.journalEntryForm.value.entryDate;

        this.journalEntry.currencyCode = this.sessionStorageService.getCountryList()?.
            find(value => value.id == this.sessionStorageService.getCompany()?.countryId)?.currencyCode ?? '';
        this.journalEntry.state = 'posted'; // Or based on some logic
        this.journalEntry.amount = 0;
        this.journalEntry.owningCompanyId = this.companyId;

        this.journalEntry.items = [];
        journalEntryData.journalItems.forEach((journalItemData: any) => {
            let index = journalEntryData.journalItems.indexOf(journalItemData);
            let detail = new JournalItem();
            detail.itemId = journalItemData.itemId;
            detail.party = journalItemData.party;

            detail.account = this.chartOfAccounts.find(value =>
                journalItemData.accountCode.split(' ')[0] === value.accountCode)!;
            detail.debit = journalItemData.debitAmount;
            detail.credit = journalItemData.creditAmount;
            detail.logisticsProjectId = journalItemData.logisticsProjectId || this.journalEntry.logisticsProjectId;
            this.journalEntry.amount += detail.debit;

            // Map dimensions from the form
            detail.dimensions = journalItemData.dimensions.map((dimData: any): Partial<JournalEntryLineDimension> => {
                return {
                    journalEntryLineDimensionId: dimData.journalEntryLineDimensionId,
                    dimensionType: { dimensionTypeId: dimData.dimensionTypeId } as DimensionType, // Send partial object
                    dimensionValueId: dimData.dimensionValue.id,
                    dimensionValueName: dimData.dimensionValue.name
                };
            });

            this.journalEntry.items.push(detail);
        });
        const externalParty = this.journalEntry.items.length === 2
            ? this.journalEntry.items.map(item => item.party).find(party => party?.id
                && (party.id.entityType !== PartyType.Company || party.id.entityId !== this.companyId))
            : undefined;
        this.journalEntry.party = externalParty
            ?? this.partyService.convertToParty(this.sessionStorageService.getCompany(), PartyType.Company, this.companyId);

        this.isSaving = true;
        const request = this.editMode
            ? this.journalEntriesService.updateJournalEntry(this.journalEntry)
            : this.journalEntriesService.createJournalEntry(this.journalEntry);
        request.pipe(finalize(() => this.isSaving = false)).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: `Journal Entry ${this.editMode ? 'updated' : 'created'} successfully` });
                this.location.back();
            },
            error: error => this.messageService.add({
                severity: 'error', summary: 'Save failed',
                detail: error?.error?.message || 'Could not save the journal entry. Please try again.'
            })
        });
    }

    cancel() {
        this.location.back(); // Go back to the previous page
    }

    onAccountSelect($event: AutoCompleteSelectEvent, i: number) {
        const account = $event.value;
        const detailGroup = (this.journalEntryForm.get('journalItems') as FormArray).controls[i] as FormGroup;
        detailGroup.patchValue({ accountCode: account.accountCode + ' ' + account.accountName });
        this.filteredChartOfAccounts = [];
        this.journalEntryForm.markAsDirty();
        this.journalEntryForm.markAsTouched();
        this.journalEntryForm.updateValueAndValidity();
    }

    removeJournalEntryDetail(index: number): void {
        (this.journalEntryForm.get('journalItems') as FormArray).removeAt(index);
    }

    onDimensionTypeChange(event: SelectChangeEvent, itemIndex: number, dimensionIndex: number): void {
        const dimensionTypeId = event.value; // The ID of the selected DimensionType
        const dimensionFormGroup = ((this.journalEntryForm.get('journalItems') as FormArray)
            .at(itemIndex).get('dimensions') as FormArray).at(dimensionIndex) as FormGroup;

        // When the type changes, reset the selected value
        dimensionFormGroup.get('dimensionValueId')?.reset();

        if (!dimensionTypeId) return;

        // Fetch dimension values from the service only if they aren't already in our cache
        if (!this.dimensionOptionsCache.has(dimensionTypeId)) { // Corrected logic starts here
            const selectedType = this.dimensionTypes.find(t => t.dimensionTypeId === dimensionTypeId);
            if (!selectedType) return;

            // Handle special, hard-coded dimension types first
            if (selectedType.name === 'Vehicle') {
                this.vehicleService.findVehiclesByTransportationAgencyId(0, 1000, this.companyId)
                    .subscribe(vehicles => {
                        // Corrected object literal syntax in .map()
                        const vehicleOptions: DimensionValue[] = vehicles.items.map(vehicle => ({
                            id: vehicle.vehicleId,
                            name: vehicle.plateNumber
                        }));
                        this.dimensionOptionsCache.set(dimensionTypeId, vehicleOptions);
                    });
            } else if (selectedType.name === 'Contract') {
                this.contractService.getAllContracts(0, 1000, this.companyId, this.companyId)
                    .subscribe(contracts => {
                        const contractOptions: DimensionValue[] = contracts.items.map(contract => ({
                            id: contract.contractID,
                            name: contract.contractNumber // Assuming contracts have a 'title'
                        }));
                        this.dimensionOptionsCache.set(dimensionTypeId, contractOptions);
                    });
            } else if (selectedType.name === 'Cost Center') {
                this.costCenterService.getAllCostCenters(this.companyId)
                    .subscribe(costCenters => {
                        const costCenterOptions: DimensionValue[] = costCenters.map(costCenter => ({
                            id: costCenter.id ?? 0,
                            name: costCenter.name
                        }));
                        this.dimensionOptionsCache.set(dimensionTypeId, costCenterOptions);
                    });
            }
        }
    }

    getDimensionOptions(itemIndex: number, dimensionIndex: number): DimensionValue[] {
        const dimensionFormGroup = (this.getDimensionsControls(itemIndex)[dimensionIndex]) as FormGroup;
        const dimensionTypeId = dimensionFormGroup.get('dimensionTypeId')?.value;
        return dimensionTypeId ? this.dimensionOptionsCache.get(dimensionTypeId) || [] : [];
    }

    subscribeToJournalItemsChanges(): void {
        this.journalItemsSub?.unsubscribe();
        const journalItemsFormArray = this.journalEntryForm.get('journalItems') as FormArray;
        this.journalItemsSub = journalItemsFormArray.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)) // Deeper comparison for arrays of objects
            )
            .subscribe(() => {
                this.calculateTotals();
            });
    }

    calculateTotals(): void {
        let debitSum = 0;
        let creditSum = 0;
        this.getJournalEntryDetailsControls.forEach(control => {
            const debit = control.get('debitAmount')?.value || 0;
            const credit = control.get('creditAmount')?.value || 0;
            debitSum += parseFloat(debit.toString()); // Ensure it's a number
            creditSum += parseFloat(credit.toString()); // Ensure it's a number
        });
        this.totalDebit = debitSum;
        this.totalCredit = creditSum;
    }

    isTotalMismatch(): boolean {
        // Using a small epsilon for floating point comparison might be safer
        // but for currency, direct comparison is usually fine if inputs are controlled.
        const tolerance = 0.001; // For example, if dealing with more precise floats
        // return Math.abs(this.totalDebit - this.totalCredit) > tolerance;
        return this.totalDebit !== this.totalCredit;
    }

    get pageTitle(): string {
        return this.editMode ? 'Edit Journal Entry' : 'New Journal Entry';
    }

    get pageSubtitle(): string {
        return this.editMode
            ? 'Update a draft accounting entry before approval and posting.'
            : 'Capture a balanced manual accounting entry with account and optional party and operational dimensions.';
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Debit', value: this.formatAmount(this.totalDebit), tone: this.isTotalMismatch() ? 'danger' : 'success' },
            { label: 'Credit', value: this.formatAmount(this.totalCredit), tone: this.isTotalMismatch() ? 'danger' : 'success' },
            { label: 'Lines', value: this.getJournalEntryDetailsControls.length, tone: 'info' },
            { label: 'Balance', value: this.isTotalMismatch() ? 'Mismatch' : 'Balanced', hint: this.formatAmount(this.totalDebit - this.totalCredit), tone: this.isTotalMismatch() ? 'danger' : 'success' }
        ];
    }

    private formatAmount(amount: number): string {
        const currencyCode = this.journalEntry.currencyCode || this.sessionStorageService.getCountryList()
            ?.find(value => value.id == this.sessionStorageService.getCompany()?.countryId)?.currencyCode || '';
        return `${currencyCode} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
    }
}
